import { FC, ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'src/components/atoms/Loader/Loader';
import { EnumRoutePaths } from 'src/configs/routes';
import { IRootState } from 'src/types/store-types';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, redirectTo = EnumRoutePaths.BASE }) => {
  const navigate = useNavigate();
  const { user, token } = useSelector((state: IRootState) => state.users);

  useEffect(() => {
    // Check if user is not authenticated (no user data and no token)
    if (!user && !token) {
      navigate(redirectTo, { replace: true });
      return;
    }

    // If we have a token but no user data, give some time for user data to load
    // This handles the case where token exists but user data is still being fetched
    if (token && !user) {
      const timeout = setTimeout(() => {
        if (!user) {
          navigate(redirectTo, { replace: true });
        }
      }, 3000); // Wait 3 seconds for user data to load

      return () => clearTimeout(timeout);
    }
    return () => console.log('');
  }, [user, token, navigate, redirectTo]);

  // Show loading while authentication is being checked
  if (token && !user) {
    return <Loader />;
  }

  // If no user and no token, component will redirect (handled by useEffect)
  if (!user && !token) {
    return <Loader />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
